// /activar-admin/:token — pantalla pública (sin auth) para activar una
// invitación admin enviada por email.
//
// Flujo:
//   1. Al montar, llama GET /api/auth/activar-admin/:token para validar el
//      token. Si OK → muestra email destino + form (nombre, apellidos, password,
//      confirm). Si error (404/410) → mensaje claro + CTA volver al inicio.
//   2. Submit → POST /api/auth/activar-admin/:token con {nombre, apellidos?,
//      password}. Tras 201 → redirect a /login con banner de éxito.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import Logo from '../components/Logo';
import { ApiError } from '../lib/api';
import { getActivarAdminInfo, postActivarAdmin } from '../lib/adminApi';

function checkPasswordPolicy(p) {
  return {
    length: p.length >= 12,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    symbol: /[^A-Za-z0-9]/.test(p),
  };
}

export default function ActivarAdmin() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [errorInicial, setErrorInicial] = useState(null);

  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [hecho, setHecho] = useState(false);

  // Validar token al montar
  useEffect(() => {
    let cancelled = false;
    setLoadingInfo(true);
    setErrorInicial(null);
    getActivarAdminInfo(token)
      .then((d) => { if (!cancelled) setInfo(d); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          const trad = t('admin.activar.errores.' + e.code, { defaultValue: '' });
          setErrorInicial(trad || e.message || t('admin.activar.errores.generico'));
        } else {
          setErrorInicial(t('admin.activar.errores.generico'));
        }
      })
      .finally(() => { if (!cancelled) setLoadingInfo(false); });
    return () => { cancelled = true; };
  }, [token, t]);

  const policy = useMemo(() => checkPasswordPolicy(password), [password]);
  const policyOk = policy.length && policy.upper && policy.lower && policy.digit && policy.symbol;
  const matches = password.length > 0 && password === password2;
  const canSubmit = policyOk && matches && nombre.trim().length > 0 && !enviando;

  const submit = async (e) => {
    e.preventDefault();
    setErrorGeneral(null);
    if (!nombre.trim()) {
      setErrorGeneral(t('admin.activar.error_nombre'));
      return;
    }
    if (!policyOk) {
      setErrorGeneral(t('admin.activar.error_weak'));
      return;
    }
    if (!matches) {
      setErrorGeneral(t('admin.activar.error_mismatch'));
      return;
    }
    setEnviando(true);
    try {
      await postActivarAdmin(token, {
        nombre: nombre.trim(),
        apellidos: apellidos.trim() || null,
        password,
      });
      setHecho(true);
      setTimeout(() => {
        navigate('/login?msg=admin_activado', { replace: true });
      }, 2200);
    } catch (err) {
      if (err instanceof ApiError) {
        const trad = t('admin.activar.errores.' + err.code, { defaultValue: '' });
        setErrorGeneral(trad || err.message || t('admin.activar.errores.generico'));
      } else {
        setErrorGeneral(t('admin.activar.errores.generico'));
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <Link to="/" className="login-logo"><Logo /></Link>
        <h1 className="login-titulo">{t('admin.activar.titulo')}</h1>

        {loadingInfo ? (
          <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.activar.validando')}</span></div>
        ) : errorInicial ? (
          <div className="login-error-block">
            <AlertCircle size={32} strokeWidth={1.5} />
            <p>{errorInicial}</p>
            <p className="login-volver">
              <Link to="/">{t('admin.activar.volver_inicio')}</Link>
            </p>
          </div>
        ) : hecho ? (
          <div className="login-success">
            <CheckCircle2 size={32} strokeWidth={1.5} />
            <p>{t('admin.activar.success')}</p>
          </div>
        ) : (
          <form className="login-form" onSubmit={submit} noValidate>
            <div className="modal-campo">
              <label>{t('admin.activar.email_destino')}</label>
              <input type="email" value={info?.email || ''} disabled readOnly />
            </div>
            <div className="modal-campo">
              <label htmlFor="aa-nombre">{t('admin.activar.nombre')} *</label>
              <input
                id="aa-nombre"
                type="text"
                autoComplete="given-name"
                autoFocus
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                disabled={enviando}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="aa-apellidos">{t('admin.activar.apellidos')}</label>
              <input
                id="aa-apellidos"
                type="text"
                autoComplete="family-name"
                value={apellidos}
                onChange={(e) => setApellidos(e.target.value)}
                disabled={enviando}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="aa-password">{t('admin.activar.password')} *</label>
              <input
                id="aa-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={enviando}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="aa-password2">{t('admin.activar.password_confirm')} *</label>
              <input
                id="aa-password2"
                type="password"
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                disabled={enviando}
              />
            </div>

            <ul className="login-policy">
              <li className={policy.length ? 'ok' : ''}>{t('admin.reset.req_length')}</li>
              <li className={policy.upper ? 'ok' : ''}>{t('admin.reset.req_upper')}</li>
              <li className={policy.lower ? 'ok' : ''}>{t('admin.reset.req_lower')}</li>
              <li className={policy.digit ? 'ok' : ''}>{t('admin.reset.req_digit')}</li>
              <li className={policy.symbol ? 'ok' : ''}>{t('admin.reset.req_symbol')}</li>
            </ul>

            {errorGeneral && (
              <div className="modal-error-general">
                <AlertCircle size={16} strokeWidth={1.75} />
                <span>{errorGeneral}</span>
              </div>
            )}

            <button type="submit" className="btn-primary login-btn" disabled={!canSubmit}>
              {enviando ? (
                <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('auth.enviando')}</>
              ) : (
                t('admin.activar.submit')
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
